import React, { type ReactElement, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Skeleton, Typography, useTheme } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import srLatn from 'date-fns/locale/sr-Latn'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import ThemeConfirmDialog from 'common/ui/theme-confirm-dialog'
import { StoreStatus } from 'common/utils/types'
import ThemeButton from 'common/ui/theme-button'
import IconButton from 'common/ui/icon-button'
import SimpleTooltip from 'common/ui/simple-tooltip'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import tendersBackground from 'assets/tendersBackground.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setErrorMessage } from 'store/app.slice'

import BidDetailsDialog from '../bid-details-dialog'
import { bidAnswer, getBidList } from '../bid.service'
import { type Bid } from '../bid.types'

import styles from './PendingBids.module.scss'

const BIDS_PER_PAGE = 9

const PendingBids: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { palette } = useTheme()
  const { bidList, count, getBidListStatus, bidAnswerStatus } = useAppSelector((state) => state.bid)
  const { vendor } = useAppSelector((state) => state.vendor)
  const [openRejectDialog, setOpenRejectDialog] = useState<boolean>(false)
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false)
  const [selectedBid, setSelectedBid] = useState<Bid | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [loadMore, setLoadMore] = useState<boolean>(false)

  const vendorProfileCompleted = useMemo<boolean>(() => {
    return !!(vendor?.topics?.length && vendor?.contact)
  }, [vendor])

  const fetchBids = (page: number): void => {
    const payload = {
      accepted: false,
      ipp: BIDS_PER_PAGE,
      page,
    }

    void dispatch(getBidList(payload))
  }

  useEffect(() => {
    fetchBids(currentPage)
  }, [])

  const loadMoreItems = (): void => {
    if (bidList.length < count) {
      const nextPage = currentPage + 1
      fetchBids(nextPage)
      setCurrentPage(nextPage)
    }
  }

  const handleScroll = (): void => {
    const distanceToBottom = document.documentElement.offsetHeight - (window.innerHeight + window.scrollY)
    if (getBidListStatus !== StoreStatus.Loading && distanceToBottom < 100) {
      setLoadMore(true)
    }
  }

  useEffect(() => {
    if (loadMore && bidList.length < count) {
      loadMoreItems()
    }
  }, [loadMore, bidList, count])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleOpenDetailsDialog = (bid: Bid): void => {
    setSelectedBid(bid)
    setOpenDetailsDialog(true)
  }

  const handleCloseDetailsDialog = (): void => {
    setSelectedBid(undefined)
    setOpenDetailsDialog(false)
  }

  const handleOpenRejectDialog = (bid: Bid): void => {
    setSelectedBid(bid)
    setOpenRejectDialog(true)
  }

  const handleCloseRejectDialog = (): void => {
    setSelectedBid(undefined)
    setOpenRejectDialog(false)
  }

  const handleRejectBid = async (): Promise<void> => {
    if (selectedBid?.id) {
      await dispatch(bidAnswer({ projectId: selectedBid?.id, answer: false })).then((action) => {
        if (bidAnswer.rejected.match(action)) {
          dispatch(setErrorMessage(action.error.message ?? 'Došlo je do greške'))
        } else {
          setOpenDetailsDialog(false)
          setOpenRejectDialog(false)
        }
      })
    }
  }

  const handleAcceptBid = async (): Promise<void> => {
    if (selectedBid?.id) {
      await dispatch(bidAnswer({ projectId: selectedBid?.id, answer: true })).then((action) => {
        if (bidAnswer.rejected.match(action)) {
          dispatch(setErrorMessage(action.error.message ?? 'Došlo je do greške'))
        } else {
          setOpenDetailsDialog(false)
          navigate(`/tenderi/${selectedBid?.id}`)
        }
      })
    }
  }

  return (
    <div
      id="bidListWrapper"
      className={styles.bidListWrapper}
      style={{
        backgroundImage: `url(${tendersBackground})`,
        backgroundPosition: 'right bottom',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {getBidListStatus === StoreStatus.Loading ? (
        Array(BIDS_PER_PAGE)
          .fill(0)
          .map((value, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              width={'100%'}
              height={96}
              sx={{ backgroundColor: palette.blue700.main, marginRight: '1rem' }}
            />
          ))
      ) : bidList.length ? (
        <>
          <div className={styles.profileIncomplete}>
            {!vendorProfileCompleted &&
              'Kako biste mogli da prihvatite tendere neophodno je da na svom profilu popunite polje za' +
                ' kontakt osobu koja će biti zadužena za komunikaciju sa konsultantom kao i Vaše oblasti rada.'}
          </div>
          {bidList.map((b) => (
            <div key={b.id} className={styles.bid}>
              <div className={styles.nameWrapper}>
                <SimpleTooltip title={b.name ?? ''} style={{ textAlign: 'left' }}>
                  <Typography className={styles.truncate}>{b.name}</Typography>
                </SimpleTooltip>
              </div>
              <div className={styles.divider} />
              <div className={styles.tenderInfoWrapper}>
                <div className={styles.info}>
                  <Typography variant="title2">ROK ZA SLANJE PONUDA</Typography>
                  <Typography variant="title5" sx={{ color: palette.gray.main }}>
                    Još {formatDistanceToNow(new Date(b.deadlineProposal), { locale: srLatn })}
                  </Typography>
                </div>
                <div className={styles.info}>
                  <Typography variant="title2">BR. TENDERA</Typography>
                  <Typography variant="title5" sx={{ color: palette.gray.main }}>
                    {b.tenderNum}
                  </Typography>
                </div>
              </div>
              <div className={styles.divider} />
              <div className={styles.buttonWrapper}>
                <ThemeButton
                  variant={BUTTON_VARIANTS.secondary}
                  size={BUTTON_SIZE.small}
                  onClick={() => {
                    handleOpenDetailsDialog(b)
                  }}
                >
                  DETALJI
                </ThemeButton>
                <IconButton
                  variant={BUTTON_VARIANTS.outlined}
                  size={BUTTON_SIZE.small}
                  sx={{
                    borderColor: palette.charts.indigo200.main,
                    width: '24px',
                    minWidth: '24px',
                    height: '24px',
                    padding: 0,
                  }}
                  onClick={() => {
                    handleOpenRejectDialog(b)
                  }}
                  icon={<ClearIcon style={{ stroke: 'white' }} />}
                />
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className={styles.noResults}>
          {vendorProfileCompleted
            ? 'Trenutno nema tendera koji odgovaraju Vašim oblastima rada.'
            : 'Kako biste mogli da prihvatite tendere neophodno je da na svom profilu popunite polje za' +
              ' kontakt osobu koja će biti zadužena za komunikaciju sa konsultantom kao i Vaše oblasti rada.'}
        </div>
      )}
      {openRejectDialog && (
        <ThemeConfirmDialog
          onClose={handleCloseRejectDialog}
          open={openRejectDialog}
          title={'Odbij zahtev za učestvovanje na projektu?'}
          status={bidAnswerStatus}
          confirmButtonText={'ODBIJ'}
          confirmButtonAction={() => {
            void handleRejectBid()
          }}
          declineButtonText={'IZAĐI'}
          description={
            'Pritiskom na dugme “Odbij” ovaj projekat će Vam se skloniti iz liste ponudjenih za davanje ponuda.'
          }
        />
      )}
      {openDetailsDialog && selectedBid && (
        <BidDetailsDialog
          bid={selectedBid}
          onClose={handleCloseDetailsDialog}
          open={openDetailsDialog}
          declineButtonAction={() => {
            void handleRejectBid()
          }}
          confirmButtonAction={() => {
            void handleAcceptBid()
          }}
        />
      )}
    </div>
  )
}

export default PendingBids
